
import type { LoanSummary, PaperlessInfo } from '@ld.consumer-portal/models';
import {
  MOCK_BALANCES,
  MOCK_DATES,
  MOCK_LOANS,
  MOCK_PROPERTIES,
  MOCK_USER,
  buildLoanAdditionalInfo,
  buildPaymentEligibility,
} from './shared.mock';

const MOCK_BORROWER_IDS = {
  mortgage: 'borrower-mortgage-12345',
  heloc: 'borrower-heloc-67890',
  homeEquity: 'borrower-homeequity-11111',
};

function buildLoanResponse(
  loan: {
    loanNumber: string;
    productType: string;
    loanStatus: number;
    daysPastDueStatus: string;
    isPaidInFull: boolean;
    isTransferredToCenlar: boolean;
    isTransferredToThirdParty: boolean;
    paymentDue: number;
    paymentDueDate: string;
    totalFeeAmount: number;
    loanBalance: {
      beginningPrincipal: number;
      unpaidPrincipal: number;
      escrowBalance: number;
    };
    property: {
      address: string;
      city: string;
      state: string;
      zip: string;
    };
    principalAndInterest: number;
    taxAndInsurance: number;
    isRepayAutopayEnabled?: boolean;
    isInhouseAutopayEnabled?: boolean;
    showPaperLessPromo?: boolean;
    availableHELOCAmount?: number;
    helocLimitAmount?: number;
    borrowerId?: string;
    canRequestPayoffQuote?: boolean;
    paperlessInfo?: PaperlessInfo;
    fees?: {
      type: number;
      code: string;
      description: string | null;
      amount: number;
      breakdown: { type: number; code: string; description: string; amount: number; breakdown: null }[] | null;
    }[];
  },
  eligibility?: { oneTime?: boolean; recurring?: boolean; biweekly?: boolean; semiMonthly?: boolean },
) {
  const defaultEligibility = {
    oneTime: eligibility?.oneTime ?? true,
    recurring: eligibility?.recurring ?? loan.loanStatus !== 2,
    biweekly: eligibility?.biweekly ?? loan.loanStatus !== 2,
    semiMonthly: eligibility?.semiMonthly ?? loan.loanStatus !== 2,
  };
  let borrowerId = loan.borrowerId;
  if (!borrowerId) {
    if (loan.loanNumber === MOCK_LOANS.mortgage.loanNumber) {
      borrowerId = MOCK_BORROWER_IDS.mortgage;
    } else if (loan.loanNumber === MOCK_LOANS.heloc.loanNumber) {
      borrowerId = MOCK_BORROWER_IDS.heloc;
    } else if (loan.loanNumber === MOCK_LOANS.homeEquity.loanNumber) {
      borrowerId = MOCK_BORROWER_IDS.homeEquity;
    } else {
      borrowerId = 'borrower-default-00000';
    }
  }

  return {
    loan: {
      loanNumber: loan.loanNumber,
      productType: loan.productType,
      loanStatus: loan.loanStatus,
      daysPastDueStatus: loan.daysPastDueStatus,
      isPaidInFull: loan.isPaidInFull,
      isTransferredToCenlar: loan.isTransferredToCenlar,
      isTransferredToThirdParty: loan.isTransferredToThirdParty,
      isEarlyTransferToCenlar: false,
      isRepayAutopayEnabled: loan.isRepayAutopayEnabled ?? true,
      isInhouseAutopayEnabled: loan.isInhouseAutopayEnabled ?? true, // Enable in-house autopay by default for testing
      showPaperLessPromo: loan.showPaperLessPromo ?? false,
      paymentDue: loan.paymentDue,
      paymentDueDate: loan.paymentDueDate,
      totalFeeAmount: loan.totalFeeAmount,
      loanBalance: loan.loanBalance,
      property: loan.property,
      paymentsDue: [
        {
          dueDate: loan.paymentDueDate,
          totalMonthlyPayment: loan.paymentDue,
          principalAndInterest: loan.principalAndInterest,
          taxAndInsurance: loan.taxAndInsurance,
        },
      ],
      fees: loan.fees ?? [],
      propertyState: loan.property.state,
      availableHELOCAmount: loan.availableHELOCAmount,
      helocLimitAmount: loan.helocLimitAmount,
      borrower: {
        id: borrowerId,
        firstName: MOCK_USER.firstName,
        lastName: MOCK_USER.lastName,
        middleName: null,
        ssn: '****1234',
        email: MOCK_USER.email,
      },
      canRequestPayoffQuote: loan.canRequestPayoffQuote ?? !loan.productType.toLowerCase().includes('heloc'),
      paperlessInfo: loan.paperlessInfo ?? {
        isTaxPaperless: false,
        isBillingPaperless: false,
        isEscrowPaperless: false,
        isOtherLettersPaperless: false,
        isEditable: true,
        isEligible: !loan.isPaidInFull && !loan.isTransferredToThirdParty,
        isEnabled: false,
      },
    },
    oneTimePaymentEligibility: buildPaymentEligibility(defaultEligibility.oneTime),
    recurringPaymentEligibility: buildPaymentEligibility(defaultEligibility.recurring),
    biweeklyRecurringPaymentEligibility: buildPaymentEligibility(defaultEligibility.biweekly),
    semiMonthlyRecurringPaymentEligibility: buildPaymentEligibility(defaultEligibility.semiMonthly),
    loanAdditionalInfo: buildLoanAdditionalInfo(),
  };
}

export const loanResponsesMock = {
  'no-loans': [],

  'single-mortgage': [
    buildLoanResponse({
      loanNumber: MOCK_LOANS.mortgage.loanNumber,
      productType: MOCK_LOANS.mortgage.productType,
      loanStatus: 0,
      daysPastDueStatus: 'On Time',
      isPaidInFull: false,
      isTransferredToCenlar: false,
      isTransferredToThirdParty: false,
      paymentDue: MOCK_BALANCES.mortgage.paymentDue,
      paymentDueDate: MOCK_DATES.paymentDueDate().toISOString(),
      totalFeeAmount: 0,
      loanBalance: {
        beginningPrincipal: MOCK_BALANCES.mortgage.beginningPrincipal,
        unpaidPrincipal: MOCK_BALANCES.mortgage.unpaidPrincipal,
        escrowBalance: MOCK_BALANCES.mortgage.escrowBalance,
      },
      property: MOCK_PROPERTIES.mortgage,
      principalAndInterest: MOCK_BALANCES.mortgage.principalAndInterest,
      taxAndInsurance: MOCK_BALANCES.mortgage.taxAndInsurance,
      isRepayAutopayEnabled: true,
      showPaperLessPromo: false,
    }),
  ],

  'multiple-loans': [
    buildLoanResponse({
      loanNumber: MOCK_LOANS.mortgage.loanNumber,
      productType: MOCK_LOANS.mortgage.productType,
      loanStatus: 0,
      daysPastDueStatus: 'On Time',
      isPaidInFull: false,
      isTransferredToCenlar: false,
      isTransferredToThirdParty: false,
      paymentDue: MOCK_BALANCES.mortgage.paymentDue,
      paymentDueDate: MOCK_DATES.paymentDueDate().toISOString(),
      totalFeeAmount: 250.0,
      loanBalance: {
        beginningPrincipal: MOCK_BALANCES.mortgage.beginningPrincipal,
        unpaidPrincipal: MOCK_BALANCES.mortgage.unpaidPrincipal,
        escrowBalance: MOCK_BALANCES.mortgage.escrowBalance,
      },
      property: MOCK_PROPERTIES.mortgage,
      principalAndInterest: MOCK_BALANCES.mortgage.principalAndInterest,
      taxAndInsurance: MOCK_BALANCES.mortgage.taxAndInsurance,
      isRepayAutopayEnabled: true,
      showPaperLessPromo: false,
      fees: [
        { type: 1, code: 'LF', description: 'Late Payment Fee', amount: 75.0, breakdown: null },
        { type: 2, code: 'NF', description: 'Returned Payment Fee', amount: 25.0, breakdown: null },
        {
          type: 3,
          code: 'OF',
          description: 'Other Fees',
          amount: 150.0,
          breakdown: [
            { type: 5, code: '4', description: 'Recording Fee', amount: 100.0, breakdown: null },
            { type: 12, code: 'E', description: 'Convenience Fee', amount: 50.0, breakdown: null },
          ],
        },
      ],
    }),
    buildLoanResponse({
      loanNumber: MOCK_LOANS.mortgageNoAutopay.loanNumber,
      productType: MOCK_LOANS.mortgageNoAutopay.productType,
      loanStatus: 0,
      daysPastDueStatus: 'On Time',
      isPaidInFull: false,
      isTransferredToCenlar: false,
      isTransferredToThirdParty: false,
      paymentDue: MOCK_BALANCES.mortgage.paymentDue,
      paymentDueDate: MOCK_DATES.paymentDueDate().toISOString(),
      totalFeeAmount: 0,
      loanBalance: {
        beginningPrincipal: MOCK_BALANCES.mortgage.beginningPrincipal,
        unpaidPrincipal: MOCK_BALANCES.mortgage.unpaidPrincipal,
        escrowBalance: MOCK_BALANCES.mortgage.escrowBalance,
      },
      property: MOCK_PROPERTIES.mortgageNoAutopay,
      principalAndInterest: MOCK_BALANCES.mortgage.principalAndInterest,
      taxAndInsurance: MOCK_BALANCES.mortgage.taxAndInsurance,
      isRepayAutopayEnabled: false, // Use internal autopay portal for testing
      showPaperLessPromo: false,
    }),
    buildLoanResponse({
      loanNumber: MOCK_LOANS.heloc.loanNumber,
      productType: MOCK_LOANS.heloc.productType,
      loanStatus: 0,
      daysPastDueStatus: 'On Time',
      isPaidInFull: false,
      isTransferredToCenlar: false,
      isTransferredToThirdParty: false,
      paymentDue: MOCK_BALANCES.heloc.paymentDue,
      paymentDueDate: MOCK_DATES.futureDate(10),
      totalFeeAmount: 25.0,
      loanBalance: {
        beginningPrincipal: MOCK_BALANCES.heloc.creditLimit,
        unpaidPrincipal: MOCK_BALANCES.heloc.unpaidPrincipal,
        escrowBalance: 0,
      },
      property: MOCK_PROPERTIES.heloc,
      principalAndInterest: MOCK_BALANCES.heloc.paymentDue,
      taxAndInsurance: 0,
      isInhouseAutopayEnabled: true,
      isRepayAutopayEnabled: true,
      showPaperLessPromo: true,
      availableHELOCAmount: MOCK_BALANCES.heloc.availableCredit,
      helocLimitAmount: MOCK_BALANCES.heloc.creditLimit,
    }),
    buildLoanResponse({
      loanNumber: MOCK_LOANS.homeEquity.loanNumber,
      productType: MOCK_LOANS.homeEquity.productType,
      loanStatus: 3,
      daysPastDueStatus: '30 Days Past Due',
      isPaidInFull: false,
      isTransferredToCenlar: false,
      isTransferredToThirdParty: false,
      paymentDue: MOCK_BALANCES.homeEquity.paymentDue,
      paymentDueDate: MOCK_DATES.pastDueDate().toISOString(),
      totalFeeAmount: 75.0,
      loanBalance: {
        beginningPrincipal: MOCK_BALANCES.homeEquity.beginningPrincipal,
        unpaidPrincipal: MOCK_BALANCES.homeEquity.unpaidPrincipal,
        escrowBalance: MOCK_BALANCES.homeEquity.escrowBalance,
      },
      property: MOCK_PROPERTIES.homeEquity,
      principalAndInterest: MOCK_BALANCES.homeEquity.principalAndInterest,
      taxAndInsurance: MOCK_BALANCES.homeEquity.taxAndInsurance,
      isRepayAutopayEnabled: false,
      showPaperLessPromo: false,
    }),
  ],

  delinquent: (() => {
    const resp = buildLoanResponse(
      {
        loanNumber: MOCK_LOANS.mortgage.loanNumber,
        productType: MOCK_LOANS.mortgage.productType,
        loanStatus: 4,
        daysPastDueStatus: '30 Days Past Due',
        isPaidInFull: false,
        isTransferredToCenlar: false,
        isTransferredToThirdParty: false,
        paymentDue: MOCK_BALANCES.mortgage.paymentDue * 2,
        paymentDueDate: MOCK_DATES.pastDueDate().toISOString(),
        totalFeeAmount: 125.0,
        loanBalance: {
          beginningPrincipal: MOCK_BALANCES.mortgage.beginningPrincipal,
          unpaidPrincipal: MOCK_BALANCES.mortgage.unpaidPrincipal,
          escrowBalance: MOCK_BALANCES.mortgage.escrowBalance,
        },
        property: MOCK_PROPERTIES.mortgage,
        principalAndInterest: MOCK_BALANCES.mortgage.principalAndInterest,
        taxAndInsurance: MOCK_BALANCES.mortgage.taxAndInsurance,
        isRepayAutopayEnabled: false,
        showPaperLessPromo: false,
      },
      { oneTime: true, recurring: false, biweekly: false, semiMonthly: false },
    );
    resp.loan.paymentsDue = [
      {
        dueDate: new Date(2026, 0, 1).toISOString(),
        totalMonthlyPayment: MOCK_BALANCES.mortgage.paymentDue,
        principalAndInterest: MOCK_BALANCES.mortgage.principalAndInterest,
        taxAndInsurance: MOCK_BALANCES.mortgage.taxAndInsurance,
      },
      {
        dueDate: new Date(2026, 1, 1).toISOString(),
        totalMonthlyPayment: MOCK_BALANCES.mortgage.paymentDue,
        principalAndInterest: MOCK_BALANCES.mortgage.principalAndInterest,
        taxAndInsurance: MOCK_BALANCES.mortgage.taxAndInsurance,
      },
    ];
    return [resp];
  })(),

  'heloc-draw': [
    buildLoanResponse({
      loanNumber: MOCK_LOANS.heloc.loanNumber,
      productType: MOCK_LOANS.heloc.productType,
      loanStatus: 0,
      daysPastDueStatus: 'On Time',
      isPaidInFull: false,
      isTransferredToCenlar: false,
      isTransferredToThirdParty: false,
      paymentDue: MOCK_BALANCES.heloc.minPayment,
      paymentDueDate: MOCK_DATES.paymentDueDate().toISOString(),
      totalFeeAmount: 0,
      loanBalance: {
        beginningPrincipal: MOCK_BALANCES.heloc.creditLimit,
        unpaidPrincipal: MOCK_BALANCES.heloc.unpaidPrincipal,
        escrowBalance: 0,
      },
      property: MOCK_PROPERTIES.heloc,
      principalAndInterest: MOCK_BALANCES.heloc.minPayment,
      taxAndInsurance: 0,
      isRepayAutopayEnabled: false,
      showPaperLessPromo: false,
      availableHELOCAmount: MOCK_BALANCES.heloc.availableCredit,
      helocLimitAmount: MOCK_BALANCES.heloc.creditLimit,
    }),
  ],

  'heloc-repayment': [
    buildLoanResponse({
      loanNumber: MOCK_LOANS.heloc.loanNumber,
      productType: MOCK_LOANS.heloc.productType,
      loanStatus: 0,
      daysPastDueStatus: 'On Time',
      isPaidInFull: false,
      isTransferredToCenlar: false,
      isTransferredToThirdParty: false,
      paymentDue: MOCK_BALANCES.heloc.paymentDue,
      paymentDueDate: MOCK_DATES.paymentDueDate().toISOString(),
      totalFeeAmount: 0,
      loanBalance: {
        beginningPrincipal: MOCK_BALANCES.heloc.creditLimit,
        unpaidPrincipal: MOCK_BALANCES.heloc.unpaidPrincipal,
        escrowBalance: 0,
      },
      property: MOCK_PROPERTIES.heloc,
      principalAndInterest: MOCK_BALANCES.heloc.paymentDue,
      availableHELOCAmount: 0,
      helocLimitAmount: MOCK_BALANCES.heloc.creditLimit,
      taxAndInsurance: 0,
      isRepayAutopayEnabled: true,
      showPaperLessPromo: false,
    }),
  ],

  'paid-in-full': [
    buildLoanResponse(
      {
        loanNumber: MOCK_LOANS.mortgage.loanNumber,
        productType: MOCK_LOANS.mortgage.productType,
        loanStatus: 0,
        daysPastDueStatus: 'On Time',
        isPaidInFull: true,
        isTransferredToCenlar: false,
        isTransferredToThirdParty: false,
        paymentDue: 0,
        paymentDueDate: '',
        totalFeeAmount: 0,
        loanBalance: {
          beginningPrincipal: MOCK_BALANCES.mortgage.beginningPrincipal,
          unpaidPrincipal: 0,
          escrowBalance: 0,
        },
        property: MOCK_PROPERTIES.mortgage,
        principalAndInterest: 0,
        taxAndInsurance: 0,
        isRepayAutopayEnabled: false,
        showPaperLessPromo: false,
      },
      { oneTime: false, recurring: false, biweekly: false, semiMonthly: false },
    ),
  ],

  transferred: [
    buildLoanResponse(
      {
        loanNumber: MOCK_LOANS.mortgage.loanNumber,
        productType: MOCK_LOANS.mortgage.productType,
        loanStatus: 0,
        daysPastDueStatus: 'On Time',
        isPaidInFull: false,
        isTransferredToCenlar: true,
        isTransferredToThirdParty: false,
        paymentDue: MOCK_BALANCES.mortgage.paymentDue,
        paymentDueDate: MOCK_DATES.paymentDueDate().toISOString(),
        totalFeeAmount: 0,
        loanBalance: {
          beginningPrincipal: MOCK_BALANCES.mortgage.beginningPrincipal,
          unpaidPrincipal: MOCK_BALANCES.mortgage.unpaidPrincipal,
          escrowBalance: MOCK_BALANCES.mortgage.escrowBalance,
        },
        property: MOCK_PROPERTIES.mortgage,
        principalAndInterest: MOCK_BALANCES.mortgage.principalAndInterest,
        taxAndInsurance: MOCK_BALANCES.mortgage.taxAndInsurance,
        isRepayAutopayEnabled: false,
        showPaperLessPromo: false,
      },
      { oneTime: false, recurring: false, biweekly: false, semiMonthly: false },
    ),
  ],

  'autopay-ineligible': [
    buildLoanResponse(
      {
        loanNumber: MOCK_LOANS.mortgage.loanNumber,
        productType: MOCK_LOANS.mortgage.productType,
        loanStatus: 0,
        daysPastDueStatus: 'On Time',
        isPaidInFull: false,
        isTransferredToCenlar: false,
        isTransferredToThirdParty: false,
        paymentDue: MOCK_BALANCES.mortgage.paymentDue,
        paymentDueDate: MOCK_DATES.paymentDueDate().toISOString(),
        totalFeeAmount: 0,
        loanBalance: {
          beginningPrincipal: MOCK_BALANCES.mortgage.beginningPrincipal,
          unpaidPrincipal: MOCK_BALANCES.mortgage.unpaidPrincipal,
          escrowBalance: MOCK_BALANCES.mortgage.escrowBalance,
        },
        property: MOCK_PROPERTIES.mortgage,
        principalAndInterest: MOCK_BALANCES.mortgage.principalAndInterest,
        taxAndInsurance: MOCK_BALANCES.mortgage.taxAndInsurance,
        isRepayAutopayEnabled: false,
        showPaperLessPromo: false,
      },
      { oneTime: true, recurring: false, biweekly: false, semiMonthly: false },
    ),
  ],

  'prepaid-three-months': (() => {
    const hardStopError = {
      exceptionType: 'HardStop',
      errorCode: 'PaymentDueDateBelongsToNextThreeMonth',
      errorMessage: 'PaymentDueDateBelongsToNextThreeMonth',
    };
    const resp = buildLoanResponse(
      {
        loanNumber: MOCK_LOANS.mortgage.loanNumber,
        productType: MOCK_LOANS.mortgage.productType,
        loanStatus: 2,
        daysPastDueStatus: 'On Time',
        isPaidInFull: false,
        isTransferredToCenlar: false,
        isTransferredToThirdParty: false,
        paymentDue: MOCK_BALANCES.mortgage.paymentDue,
        paymentDueDate: MOCK_DATES.paymentDueDate().toISOString(),
        totalFeeAmount: 0,
        loanBalance: {
          beginningPrincipal: MOCK_BALANCES.mortgage.beginningPrincipal,
          unpaidPrincipal: MOCK_BALANCES.mortgage.unpaidPrincipal,
          escrowBalance: MOCK_BALANCES.mortgage.escrowBalance,
        },
        property: MOCK_PROPERTIES.mortgage,
        principalAndInterest: MOCK_BALANCES.mortgage.principalAndInterest,
        taxAndInsurance: MOCK_BALANCES.mortgage.taxAndInsurance,
        isRepayAutopayEnabled: true,
        showPaperLessPromo: false,
      },
      { oneTime: true, recurring: false, biweekly: false, semiMonthly: false },
    );
    resp.recurringPaymentEligibility = buildPaymentEligibility(false, [hardStopError]);
    resp.biweeklyRecurringPaymentEligibility = buildPaymentEligibility(false, [hardStopError]);
    resp.semiMonthlyRecurringPaymentEligibility = buildPaymentEligibility(false, [hardStopError]);
    return [resp];
  })(),

  'payoff-ineligible': [
    buildLoanResponse({
      loanNumber: MOCK_LOANS.mortgage.loanNumber,
      productType: MOCK_LOANS.mortgage.productType,
      loanStatus: 0,
      daysPastDueStatus: 'On Time',
      isPaidInFull: false,
      isTransferredToCenlar: false,
      isTransferredToThirdParty: false,
      paymentDue: MOCK_BALANCES.mortgage.paymentDue,
      paymentDueDate: MOCK_DATES.paymentDueDate().toISOString(),
      totalFeeAmount: 0,
      loanBalance: {
        beginningPrincipal: MOCK_BALANCES.mortgage.beginningPrincipal,
        unpaidPrincipal: MOCK_BALANCES.mortgage.unpaidPrincipal,
        escrowBalance: MOCK_BALANCES.mortgage.escrowBalance,
      },
      property: MOCK_PROPERTIES.mortgage,
      principalAndInterest: MOCK_BALANCES.mortgage.principalAndInterest,
      taxAndInsurance: MOCK_BALANCES.mortgage.taxAndInsurance,
      isRepayAutopayEnabled: true,
      showPaperLessPromo: false,
      canRequestPayoffQuote: false,
    }),
  ],
};

export const loanSummaryMock: Record<string, LoanSummary> = {
  default: {
    loanNumber: MOCK_LOANS.mortgage.loanNumber,
    interestRate: 3.875,
    principalBalance: MOCK_BALANCES.mortgage.unpaidPrincipal,
    maturityDate: '2053-01-15T00:00:00',
    typeOfMortgage: 'Conventional Fixed',
    propertyAddress: `${MOCK_PROPERTIES.mortgage.address}, ${MOCK_PROPERTIES.mortgage.city}, ${MOCK_PROPERTIES.mortgage.state} ${MOCK_PROPERTIES.mortgage.zip}`,
    principalInterestAmount: MOCK_BALANCES.mortgage.principalAndInterest,
    taxAndInsuranceAmount: MOCK_BALANCES.mortgage.taxAndInsurance,
    escrowBalance: MOCK_BALANCES.mortgage.escrowBalance,
  },

  heloc: {
    loanNumber: MOCK_LOANS.heloc.loanNumber,
    interestRate: 5.25,
    principalBalance: MOCK_BALANCES.heloc.unpaidPrincipal,
    maturityDate: '2033-06-01T00:00:00',
    typeOfMortgage: 'HELOC',
    propertyAddress: `${MOCK_PROPERTIES.heloc.address}, ${MOCK_PROPERTIES.heloc.city}, ${MOCK_PROPERTIES.heloc.state} ${MOCK_PROPERTIES.heloc.zip}`,
    principalInterestAmount: MOCK_BALANCES.heloc.minPayment,
    taxAndInsuranceAmount: 0,
    escrowBalance: 0,
  },

  'home-equity': {
    loanNumber: MOCK_LOANS.homeEquity.loanNumber,
    interestRate: 4.5,
    principalBalance: MOCK_BALANCES.homeEquity.unpaidPrincipal,
    maturityDate: '2038-03-20T00:00:00',
    typeOfMortgage: 'Home Equity Fixed',
    propertyAddress: `${MOCK_PROPERTIES.homeEquity.address}, ${MOCK_PROPERTIES.homeEquity.city}, ${MOCK_PROPERTIES.homeEquity.state} ${MOCK_PROPERTIES.homeEquity.zip}`,
    principalInterestAmount: MOCK_BALANCES.homeEquity.principalAndInterest,
    taxAndInsuranceAmount: MOCK_BALANCES.homeEquity.taxAndInsurance,
    escrowBalance: MOCK_BALANCES.homeEquity.escrowBalance,
  },
};
